import React, { useEffect, useMemo, useRef, useState } from "react";

// AG Grid v34
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-quartz.css";

import {
  Box,
  Heading,
  HStack,
  Select,
  IconButton,
  Flex,
  Image,
  Stack,
  useToast,
  Tooltip,
  FormControl,
  SimpleGrid,
  FormLabel,
  Input,
  Button,
  Spacer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import {
  HiOutlineFolderOpen,
  HiOutlinePlus,
  HiOutlineSave,
  HiOutlineTrash,
  HiDocumentDuplicate,
} from "react-icons/hi";
import { useAuthenticatedRequest } from "./hooks/useAuthenticatedRequest";
import { showApiErrorToast } from "./utils/showApiToast";

import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import CodesetSelect from "./CodesetSelect";

import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
ModuleRegistry.registerModules([AllCommunityModule]);

/* ---------------------------------------
   Helpers
----------------------------------------*/
const uuid = () =>
  (crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

const withLocalIds = (rows = []) =>
  rows.map((r) => ({ id: r.id || r.uuid || uuid(), ...r }));

const API_ROOT = "https://codex.mrc.ai/api/codelists";

const newCodeListTemplate = () => ({
  _id: undefined, // will be set by backend on POST
  name: "New CodeList",
  dataStandard: "",
  dataStandardVersion: "",
  agency: "",
  tableID: "NEW",
  filter: "",
  notes: "",
  locked: "",
  codes: [],
  createdDate: new Date().toISOString(),
  _isNew: true, // local flag (not sent)
});

/* ---------------------------------------
   Component
----------------------------------------*/
const Codex = () => {
  const toast = useToast();
  const gridRef = useRef(null);
  const { post } = useAuthenticatedRequest();

  const [codeLists, setCodeLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState("");
  const [codeList, setCodeList] = useState(null);

  const [quickFilterText, setQuickFilterText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Copy modal state
  const [newCodeListId, setNewCodeListId] = useState("");
  const [copying, setCopying] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  /* ---------------------------------------
     Load list of codelists
  ----------------------------------------*/
  const refreshCodeLists = async () => {
    try {
      const res = await fetch(API_ROOT);
      const data = await res.json();
      setCodeLists(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error refreshing code lists", err);
      toast({
        title: "Error refreshing code lists",
        status: "error",
        duration: 3000,
      });
    }
  };

  useEffect(() => {
    refreshCodeLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------------------------------
     Load one list by tableID (business key)
  ----------------------------------------*/
  const handleLoadCodes = async (tableID) => {
    if (!tableID) return;
    try {
      const res = await fetch(`${API_ROOT}/${encodeURIComponent(tableID)}`);
      if (!res.ok) throw new Error("Fetch failed");
      const data = await res.json();

      const codes = (data?.codes || []).map((r) => ({
        id: r.id || r.uuid || uuid(), // stable local key for grid
        ...r,
      }));

      setCodeList({ ...data, codes, _isNew: false });
      setIsDirty(false);
    } catch (err) {
      console.error("Error fetching codes", err);
      toast({
        title: "Error fetching codes",
        status: "error",
        duration: 3000,
      });
    }
  };

  /* ---------------------------------------
     New CodeList shell
  ----------------------------------------*/
  const handleAddCodeList = () => {
    const next = newCodeListTemplate();
    setCodeList(next);
    setSelectedListId("NEW");
    setIsDirty(true);
  };

  /* ---------------------------------------
     Add / delete row
  ----------------------------------------*/
  const handleAddRow = () => {
    if (!codeList) return;
    const newRow = {
      id: uuid(), // local-only id for grid
      tableID: codeList.tableID || "",
      longCode: "",
      typeCode: "",
      camelCode: "",
      description1: "",
      inUse: "Yes",
      status: "",
      filter: "",
      codeList: "",
      codeListAgency: "",
      codeListFilter: "",
      unitCode: "",
      createdDate: new Date().toISOString(),
    };

    const currentLength = codeList?.codes?.length || 0;

    setCodeList((prev) =>
      prev ? { ...prev, codes: [...(prev.codes || []), newRow] } : prev
    );
    setIsDirty(true);

    // Scroll to the newly added row
    queueMicrotask(() => {
      const api = gridRef.current?.api;
      if (api) {
        api.ensureIndexVisible(currentLength);
        setTimeout(() => {
          api.ensureIndexVisible(currentLength);
        }, 100);
      }
    });
  };

  const handleDeleteRow = (rowId) => {
    if (!codeList?.codes?.length) return;
    setCodeList({
      ...codeList,
      codes: codeList.codes.filter((r) => r.id !== rowId),
    });
    setIsDirty(true);
  };

  /* ---------------------------------------
     Save handler (POST/PUT)
     - POST: /codelists        (no _id, backend creates)
     - PUT:  /codelists/{_id}  (GUID)
  ----------------------------------------*/
  const handleSave = async () => {
    if (!codeList) return;

    // ✅ Validation
    if (!codeList.tableID?.trim()) {
      toast({
        title: "Code List ID is required",
        description: "Please enter a Table ID (e.g., A82)",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (!codeList.name?.trim()) {
      toast({
        title: "Name is required",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    const isNew = codeList._isNew === true;

    setIsSaving(true);

    try {
      // Normalize rows and strip grid-only local id
      const normalizedCodes = (codeList.codes || []).map(({ id, ...rest }) => {
        const normalized = {
          ...rest,
          tableID: codeList.tableID,
        };

        // Only include uuid if it's a valid GUID
        if (
          rest.uuid &&
          typeof rest.uuid === "string" &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            rest.uuid
          )
        ) {
          normalized.uuid = rest.uuid;
        }

        // Only include _id if it exists (for existing codes)
        if (
          rest._id &&
          typeof rest._id === "string" &&
          rest._id.length > 0
        ) {
          normalized._id = rest._id;
        }

        return normalized;
      });

      // ✅ Clean payload
      const payload = {
        name: codeList.name,
        dataStandard: codeList.dataStandard || "",
        dataStandardVersion: codeList.dataStandardVersion || "",
        agency: codeList.agency || "",
        tableID: codeList.tableID,
        filter: codeList.filter || "",
        notes: codeList.notes || "",
        locked: codeList.locked || "",
        codes: normalizedCodes,
      };

      // For update, include _id so model matches backend expectations
      if (!isNew && codeList._id) {
        payload._id = codeList._id;
      }

      const method = isNew ? "POST" : "PUT";
      const url = isNew
        ? API_ROOT
        : `${API_ROOT}/${encodeURIComponent(codeList._id)}`;

      console.log("🔍 Saving CodeList:", {
        method,
        url,
        isNew,
        tableID: codeList.tableID,
        _id: codeList._id,
      });

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to ${method} code list: ${errorText}`);
      }

      // ✅ Always re-fetch to get the authoritative server state
      const refetchUrl = `${API_ROOT}/${encodeURIComponent(
        codeList.tableID
      )}`;
      console.log(`🔄 Re-fetching from:`, refetchUrl);

      const refetchRes = await fetch(refetchUrl);

      if (!refetchRes.ok) {
        console.warn("Save succeeded but re-fetch failed");
        // Fall back to using the payload + existing _id
        const fallback = {
          ...payload,
          _id: codeList._id,
          codes: withLocalIds(payload.codes || []),
          _isNew: false,
        };
        setCodeList(fallback);
        setIsDirty(false);
      } else {
        const saved = await refetchRes.json();
        const fixed = {
          ...saved,
          codes: withLocalIds(saved.codes || []),
          _isNew: false,
        };

        console.log("✅ Loaded fresh data:", {
          hasId: !!fixed._id,
          idValue: fixed._id,
          tableID: fixed.tableID,
        });

        setCodeList(fixed);
        setIsDirty(false);
      }

      // Refresh dropdown and select the item
      await refreshCodeLists();
      setSelectedListId(codeList.tableID);

      toast({
        title: isNew ? "CodeList created" : "CodeList updated",
        description: `Successfully saved "${codeList.name}"`,
        status: "success",
        duration: 3000,
      });
    } catch (err) {
      console.error("❌ Save error:", err);
      toast({
        title: "Error saving codes",
        status: "error",
        description: err?.message || "Unknown error",
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  /* ---------------------------------------
     Copy CodeList handlers
  ----------------------------------------*/
  const handleCopyClick = () => {
    setNewCodeListId("");
    onOpen();
  };

  const handleCopyCodeList = async () => {
    if (!newCodeListId.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a new Code List ID",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setCopying(true);
    try {
      await post(
        `${API_ROOT}/${encodeURIComponent(codeList.tableID)}/copy`,
        { newCodeList: newCodeListId.trim() }
      );

      toast({
        title: "CodeList copied",
        description: `Successfully copied "${codeList.name}" to "${newCodeListId}"`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onClose();

      // Refresh the list and select the new codelist
      await refreshCodeLists();
      setSelectedListId(newCodeListId.trim());
      await handleLoadCodes(newCodeListId.trim());
    } catch (error) {
      showApiErrorToast(toast, error, { title: "Error copying CodeList" });
    } finally {
      setCopying(false);
    }
  };

  const handleCopyModalClose = () => {
    if (!copying) {
      onClose();
      setNewCodeListId("");
    }
  };

  /* ---------------------------------------
     AG Grid config
  ----------------------------------------*/

  // Action cell (row-level delete)
  const ActionCell = (params) => {
    const id = params?.data?.id;
    return (
      <Tooltip label="Delete row">
        <IconButton
          aria-label="Delete row"
          icon={<HiOutlineTrash />}
          size="xs"
          variant="ghost"
          onClick={(e) => {
            e?.stopPropagation?.();
            handleDeleteRow(id);
          }}
        />
      </Tooltip>
    );
  };

  const columnDefs = useMemo(
    () => [
      { headerName: "TableID", field: "tableID", hide: true },
      { headerName: "Agency", field: "codeListAgency", editable: true, width: 200 },
      { headerName: "LongCode", field: "longCode", editable: true, minWidth: 150 },
      {
        headerName: "Description",
        field: "description1",
        editable: true,
        flex: 1,
        minWidth: 220,
      },
      { headerName: "InUse", field: "inUse", editable: true, width: 100 },
      { headerName: "Filter", field: "filter", editable: true, width: 180 },
      { headerName: "Unit", field: "unitCode", editable: true, width: 110 },
      { headerName: "TypeCode", field: "typeCode", editable: true, minWidth: 150 },
      { headerName: "CamelCode", field: "camelCode", editable: true, minWidth: 150 },
      { headerName: "CodeList", field: "codeList", editable: true, width: 130 },
      {
        headerName: "",
        field: "__actions",
        maxWidth: 50,
        cellRenderer: ActionCell,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
      },
      { headerName: "ID (hidden)", field: "id", hide: true },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      floatingFilter: true,
      resizable: true,
      suppressHeaderMenuButton: false,
    }),
    []
  );

  const autoSizeStrategy = useMemo(
    () => ({ type: "fitGridWidth", defaultMinWidth: 80 }),
    []
  );

  const rowData = useMemo(() => codeList?.codes || [], [codeList]);

  const onGridReady = () => {
    const api = gridRef.current?.api;
    if (api && quickFilterText) {
      api.setGridOption("quickFilterText", quickFilterText);
    }
  };

  const onCellValueChanged = (params) => {
    const rowId = params.data?.id;
    const field = params.colDef?.field;
    const newValue = params.newValue;
    if (!rowId || !field) return;

    setCodeList((prev) => {
      if (!prev) return prev;
      const next = prev.codes.map((r) =>
        r.id === rowId ? { ...r, [field]: newValue } : r
      );
      return { ...prev, codes: next };
    });
    setIsDirty(true);
  };

  const handleQuickFilter = (e) => {
    const val = e.target.value;
    setQuickFilterText(val);
    gridRef.current?.api?.setGridOption("quickFilterText", val);
  };

  // Sort code lists by tableID for the dropdown
  const sortedCodeLists = useMemo(() => {
    return [...codeLists].sort((a, b) => {
      const aID = a.tableID || "";
      const bID = b.tableID || "";
      return aID.localeCompare(bID);
    });
  }, [codeLists]);

  /* ---------------------------------------
     Render
  ----------------------------------------*/
  return (
    <Box>
      <Navbar />

      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={10}>
          {/* Toolbar / selector */}
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={3}
            mb={3}
            borderColor="gray.500"
            bg="whiteAlpha.800"
          >
            <Flex mb={2} align="center">
              <HStack spacing={6}>
                <Heading size="lg">Codex</Heading>
              </HStack>
            </Flex>

            <Stack direction="row" spacing={4} align="center">
              <Select
                placeholder="Select Code List"
                width="auto"
                value={selectedListId}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedListId(v);
                  handleLoadCodes(v);
                }}
              >
                {sortedCodeLists.map((list) => (
                  <option key={list.tableID} value={list.tableID}>
                    {list.tableID} - {list.name}
                  </option>
                ))}
              </Select>

              <Tooltip label="Load Codes">
                <IconButton
                  aria-label="Load Codes"
                  icon={<HiOutlineFolderOpen />}
                  onClick={() => handleLoadCodes(selectedListId)}
                />
              </Tooltip>

              <Tooltip label="Create a new CodeList">
                <IconButton
                  aria-label="Add CodeList"
                  icon={<HiOutlinePlus />}
                  onClick={handleAddCodeList}
                />
              </Tooltip>

              <Button
                leftIcon={<HiOutlineSave />}
                onClick={handleSave}
                isLoading={isSaving}
                colorScheme={isDirty ? "blue" : undefined}
                variant={isDirty ? "solid" : "outline"}
                isDisabled={!codeList}
              >
                Save
              </Button>

              <Tooltip label="Copy CodeList">
                <IconButton
                  aria-label="Copy CodeList"
                  icon={<HiDocumentDuplicate />}
                  onClick={handleCopyClick}
                  isDisabled={!codeList || codeList._isNew}
                />
              </Tooltip>
            </Stack>
          </Box>

          {/* Top-level fields */}
          {codeList && (
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={3}
              mb={3}
              borderColor="gray.500"
              bg="whiteAlpha.800"
            >
              <HStack mb={4} align="start" spacing={4} flexWrap="wrap">
                <FormControl width={{ base: "100%", md: "50%" }}>
                  <FormLabel>Name</FormLabel>
                  <Input
                    value={codeList.name || ""}
                    onChange={(e) => {
                      setCodeList({ ...codeList, name: e.target.value });
                      setIsDirty(true);
                    }}
                    size="md"
                  />
                </FormControl>
              </HStack>

              <SimpleGrid columns={{ base: 1, md: 5 }} spacing={4} mt={2}>
                <FormControl>
                  <FormLabel>Code List ID</FormLabel>
                  <Input
                    value={codeList.tableID || ""}
                    onChange={(e) => {
                      setCodeList({ ...codeList, tableID: e.target.value });
                      setIsDirty(true);
                    }}
                    size="sm"
                  />
                </FormControl>

                <CodesetSelect
                  label="CodeList Agency"
                  codesetCode="3055"
                  value={
                    codeList.agency
                      ? { code: codeList.agency, description: "" }
                      : null
                  }
                  onChange={(selected) => {
                    setCodeList({ ...codeList, agency: selected.code });
                    setIsDirty(true);
                  }}
                  size="sm"
                  fontSize="sm"
                />

                <FormControl>
                  <FormLabel>Data Standard</FormLabel>
                  <Input
                    value={codeList.dataStandard || ""}
                    onChange={(e) => {
                      setCodeList({
                        ...codeList,
                        dataStandard: e.target.value,
                      });
                      setIsDirty(true);
                    }}
                    size="sm"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Data Standard Version</FormLabel>
                  <Input
                    value={codeList.dataStandardVersion || ""}
                    onChange={(e) => {
                      setCodeList({
                        ...codeList,
                        dataStandardVersion: e.target.value,
                      });
                      setIsDirty(true);
                    }}
                    size="sm"
                  />
                </FormControl>

                <CodesetSelect
                  label="Locked"
                  codesetCode="JVRI008"
                  value={
                    codeList.locked
                      ? { code: codeList.locked, description: "" }
                      : null
                  }
                  onChange={(selected) => {
                    setCodeList({ ...codeList, locked: selected.code });
                    setIsDirty(true);
                  }}
                  size="sm"
                  fontSize="sm"
                />
              </SimpleGrid>

              <FormControl mt={4}>
                <FormLabel>Notes</FormLabel>
                <Input
                  value={codeList.notes || ""}
                  onChange={(e) => {
                    setCodeList({ ...codeList, notes: e.target.value });
                    setIsDirty(true);
                  }}
                  size="sm"
                />
              </FormControl>
            </Box>
          )}

          {codeList && (
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={3}
              mb={1}
              borderColor="gray.500"
              bg="whiteAlpha.800"
            >
              <Flex align="center" gap={4}>
                <Tooltip label="Add Code row">
                  <IconButton
                    aria-label="Add Code"
                    icon={<HiOutlinePlus />}
                    onClick={handleAddRow}
                  />
                </Tooltip>
                <Spacer />
                <Input
                  placeholder="Quick filter…"
                  value={quickFilterText}
                  onChange={handleQuickFilter}
                  width="320px"
                  bg="white"
                  size="sm"
                />
              </Flex>
            </Box>
          )}

          {/* Grid */}
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            bg="whiteAlpha.800"
          >
            {codeList && (
              <div
                className="ag-theme-quartz"
                style={{ width: "100%", height: "70vh" }}
              >
                <AgGridReact
                  ref={gridRef}
                  theme="quartz"
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowData={rowData}
                  autoSizeStrategy={autoSizeStrategy}
                  rowSelection={{ mode: "multiRow", checkboxes: false }}
                  suppressRowClickSelection={false}
                  getRowId={(p) => (p.data && p.data.id) || uuid()}
                  pagination
                  paginationPageSize={50}
                  animateRows
                  onGridReady={onGridReady}
                  onCellValueChanged={onCellValueChanged}
                />
              </div>
            )}
          </Box>
        </Box>
      </Box>

      {/* Copy CodeList Modal */}
      <Modal isOpen={isOpen} onClose={handleCopyModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Copy CodeList</ModalHeader>
          <ModalCloseButton isDisabled={copying} />
          <ModalBody>
            <Text mb={4}>
              Copy all codes from <strong>{codeList?.name}</strong> ({codeList?.tableID}) to a new CodeList.
            </Text>
            <FormControl>
              <FormLabel>New Code List ID</FormLabel>
              <Input
                placeholder="Enter new Code List ID"
                value={newCodeListId}
                onChange={(e) => setNewCodeListId(e.target.value)}
                isDisabled={copying}
                autoFocus
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCopyModalClose} isDisabled={copying}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCopyCodeList}
              isLoading={copying}
              loadingText="Copying..."
            >
              Copy
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Codex;
